import { matchPath } from 'react-router-dom'

import { RouteParamValue } from './appRoutes'
import { EnumConstraint, EnumKey, keysOf, keysOfNumericEnum } from './tsUtils'
import { buildObjectFromKeys } from './objectUtils'
import { IntegrationTypeKey } from '../types/integrationTypeUtils'

export type RouteWithParams<TParams extends Record<string, RouteParamValue>> = {
  // NOTE: This can't be a literal string - it's constructed in runtime from a template string and type params
  readonly template: string

  readonly makeUrl: (routeArgs: TParams) => string
}

export type RouteOptions<
  TEnum extends EnumConstraint<TEnum>,
  TParams extends Record<string, number | EnumKey<TEnum>>> = {
  [K in keyof TParams]: TParams[K] extends number ? null : TEnum
}

/**
 * Constraints type T to to only have values of type TValue.
 */
export type ValuesConstraint<T extends object, TValue> =
  Record<keyof T, TValue>

/**
 * Creates a route with params from an interpolated string, which must start with a slash.
 * All params are required, optional params aren't allowed.
 * Must have a single enum param and zero or more number params.
 */
export const createRoute =
  <TEnum extends EnumConstraint<TEnum>,
    TParams extends ValuesConstraint<TParams, number | EnumKey<TEnum>>>
  (options: RouteOptions<TEnum, TParams>, prefix?: string) =>
    <TParamName extends (string & keyof TParams)[]>
    (templateFragmentsAll: TemplateStringsArray, ...routeParamNames: TParamName): RouteWithParams<TParams> => {
      // Omit empty fragments
      const templateFragments = templateFragmentsAll.filter(s => !!s)

      if (templateFragments.length === 0) {
        throw new Error('All template fragments are empty (route template must start with a slash)')
      }

      if (!templateFragments[0].startsWith('/')) {
        throw new Error(`The route template must start with a slash: ${templateFragments[0]}`)
      }

      if (routeParamNames.length === 0) {
        throw new Error('No route params')
      }

      if (routeParamNames.length !== templateFragments.length) {
        throw new Error(`Lengths of route params and template fragments do not match`)
      }

      for (const routeParamName of routeParamNames) {
        if (!/^\w+$/.test(routeParamName)) {
          throw new Error(`Bad route template param: ${routeParamName}`)
        }
      }

      if (prefix) {
        if (!prefix.startsWith('/')) {
          throw new Error(`The route prefix must start with a slash: ${prefix}`)
        }

        if (!/^\/[\w/-]+$/.test(prefix)) {
          throw new Error(`Bad route prefix: ${prefix}`)
        }
      }

      for (const templateFragment of templateFragments) {
        if (!/^[\w/-]+$/.test(templateFragment)) {
          throw new Error(`Bad route template fragment: ${templateFragment}`)
        }
      }

      // Join string template fragments with route params - they match 1:1, validated above
      const template =
        templateFragments.reduce(
          (result, fragment, index) => {
            const paramName = routeParamNames[index]
            const paramOption = options[paramName]

            let paramRegex: string

            // Restrict to either numbers or enum keys
            if (paramOption == null) {
              paramRegex = '\\d+'
            } else {
              const enumKeys = keysOfNumericEnum(paramOption)

              for (const enumKey of enumKeys) {
                if (!/^\w+$/.test(enumKey)) {
                  throw new Error(`Bad enum key: ${enumKey}`)
                }
              }

              paramRegex = enumKeys.join('|')
            }

            return `${result}${fragment}:${paramName}(${paramRegex})`
          },
          prefix ?? '')

      return {
        template,

        makeUrl: (routeParams) => {
          const paramEntries = Object.entries(routeParams)

          return templateFragments.reduce(
            (result, fragment, index) => {
              const paramName = routeParamNames[index]

              const paramEntry = paramEntries.find(([routeKey]) => routeKey === paramName)

              if (!paramEntry) {
                throw new Error(
                  `Missing route param: ${paramName} in ${paramEntries.map(([key]) => key).join(', ')}`)
              }

              let [, paramValue] = paramEntry

              switch (typeof paramValue) {
                case 'number':
                case 'string':
                  return result + fragment + String(paramValue)

                default:
                  throw new Error(`Bad route param type for '${paramName}: ${typeof paramValue} / ${paramValue}`)
              }
            },
            prefix ?? '')
        }
      } as const
    }

export type LegacyRouteWithParams<TParams extends Record<string, unknown>> = {
  template: string
  route: (routeArgs: TParams) => string
  parse: (url: string) => Partial<TParams> | null
  makeUrl: (routeArgs: TParams) => string
}

// TODO: Replace with useParams - see MoySkladAppAuthScreen. After - enable ESLint no-type-assertion.
export const createStaticRoute = (path: string): LegacyRouteWithParams<Record<string, never>> => {
  const makeUrl = () => path

  return {
    template: path,
    route: makeUrl,
    makeUrl,
    parse: () => {
      throw new Error('Static routes do not support parsing.')
    }
  }
}

export function parseUrl<TParams extends Record<string, unknown>>(
  template: string,
  url: string
): Partial<TParams> | null {
  const templateParts = template.split('/').filter(Boolean)
  const urlParts = url.split('/').filter(Boolean)

  const maxPartsToCheck = Math.min(templateParts.length, urlParts.length)

  const truncatedTemplate = templateParts.slice(0, maxPartsToCheck).join('/')
  const truncatedUrl = urlParts.slice(0, maxPartsToCheck).join('/')

  const match = matchPath(truncatedUrl, {path: truncatedTemplate})

  if (!match) {
    return null
  }
  const params: Partial<TParams> = {}

  for (let i = 0; i < templateParts.length; i++) {
    const templatePart = templateParts[i]
    const urlPart = urlParts[i]

    if (templatePart.startsWith(':')) {
      if (urlPart === undefined) {
        return null
      }

      // eslint-disable-next-line no-type-assertion/no-type-assertion
      const paramName = templatePart.slice(1) as keyof TParams
      if (paramName === 'integration') {
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        params[paramName] = decodeURIComponent(
          urlPart) as unknown as TParams[keyof TParams & 'integration' | IntegrationTypeKey]
      } else {
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        params[paramName] = decodeURIComponent(urlPart) as TParams[keyof TParams]
      }
    }
  }

  return params
}

/**
 * MpCard legacy route API with route/parse and string params.
 */
export const createLegacyRoute =
  <TParams extends Record<string, unknown>>() =>
    <TParamName extends (string & keyof TParams)[]>
    (templateFragmentsAll: TemplateStringsArray, ...routeParamNames: TParamName): LegacyRouteWithParams<TParams> => {
      const templateFragments = templateFragmentsAll.filter(s => !!s)

      if (routeParamNames.length === 0) {
        throw new Error('No route params')
      }

      if (routeParamNames.length !== templateFragments.length) {
        throw new Error(`Lengths of route params and template fragments do not match`)
      }

      if (!templateFragments[0].startsWith('/')) {
        throw new Error(`The route template must start with a slash: ${templateFragments[0]}`)
      }

      for (const templateFragment of templateFragments) {
        if (!/^[\w/]+$/.test(templateFragment)) {
          throw new Error(`Bad route template fragment: ${templateFragment}`)
        }
      }

      const template =
        templateFragments.reduce(
          (acc, fragment, index) => acc + fragment + `:${routeParamNames[index]}`, '')

      const route = (routeParams: TParams) => {
        const routeEntries = Object.entries(routeParams)

        return templateFragments.reduce(
          (acc, fragment, index) => {
            const routeParamName = routeParamNames[index]

            const routeEntry = routeEntries.find(([key]) => key === routeParamName)

            if (!routeEntry) {
              throw new Error(`Missing route param: ${routeParamName}`)
            }

            const [, value] = routeEntry

            return acc + fragment + String(value)
          }, '')
      }

      return {
        template,
        route,
        makeUrl: route,
        parse: url => parseUrl<TParams>(template, url)
      }
    }

/**
 * Combines a base route with a suffix.
 *
 * @template TBaseParams - The type of parameters for the route.
 *
 * @param route - The base route object.
 * @param suffix - A string template to append to the base route.
 */
export const createSubRoute =
  <TParams extends Record<string, RouteParamValue>>
  (route: RouteWithParams<TParams>, suffix: string) => {
    if (!suffix.startsWith('/')) {
      throw new Error(`Subroute must start with a slash: ${suffix}`)
    }

    return ({
      template: route.template + suffix,
      makeUrl: (params: TParams) => route.makeUrl(params) + suffix
    } as const satisfies RouteWithParams<TParams>)
  }

/**
 * Adds a route property for each of subRoutes.
 * NOTE: Not composable, don't nest calls to this function.
 */
export const withSubRoutes =
  <TParams extends Record<string, RouteParamValue>, T extends string[]>
  (baseRoute: RouteWithParams<TParams>, ...subRoutes: T) => {
    const baseKeys = keysOf(baseRoute)
    const baseSubRoutes = baseKeys.filter(_ => subRoutes.includes(_))

    if (baseSubRoutes.length) {
      throw new Error(`Subroutes must not contain keys of baseRoute: ${baseSubRoutes.join(', ')}.`)
    }

    const badSubRoutes = subRoutes.filter(_ => !/^\w+$/.test(_))

    if (badSubRoutes.length) {
      throw new Error(`Subroutes must be alphanumeric: ${badSubRoutes.join(', ')}.`)
    }

    return {
      ...baseRoute,
      ...buildObjectFromKeys<RouteWithParams<TParams>, T>(
        subRoute => createSubRoute(baseRoute, `/${subRoute}`),
        ...subRoutes)
    } as const
  }